import type { VerifyTelegramActivationDto } from "../types/telegram-activation";

export function validateTelegramChatId(chatId: number) {
  if (!Number.isSafeInteger(chatId) || chatId === 0) throw new Error("Telegram chat id not available.");
}

export function telegramActivationIdentity(telegramChatId: number, telegramUserId?: number) {
  validateTelegramChatId(telegramChatId);
  if (telegramUserId !== undefined && (!Number.isSafeInteger(telegramUserId) || telegramUserId <= 0)) {
    throw new Error("telegramUserId must be a positive integer. Reopen this app through the trusted bot.");
  }
  if (telegramChatId < 0 && telegramUserId === undefined) {
    throw new Error("Telegram user identity is missing. Open this app from your Telegram account and try again.");
  }
  if (telegramChatId > 0 && telegramUserId !== undefined && telegramUserId !== telegramChatId) {
    throw new Error("telegramUserId must match telegramChatId for private chats. Reopen this app from your own chat.");
  }
  return { telegramChatId, ...(telegramUserId !== undefined ? { telegramUserId } : {}) };
}

export function telegramLaunchActivationIdentity(telegramChatId: number, initData: string, telegramUserId?: number) {
  if (telegramChatId < 0 && telegramUserId === undefined) {
    let user: { id?: number; is_bot?: boolean } | null;
    try { user = JSON.parse(new URLSearchParams(initData).get("user") || "null"); }
    catch { throw new Error("Invalid Telegram user identity. Reopen this app through Telegram."); }
    if (user?.is_bot) throw new Error("A Telegram bot cannot act as the activation user.");
    return telegramActivationIdentity(telegramChatId, user?.id);
  }
  return telegramActivationIdentity(telegramChatId, telegramUserId);
}

export function telegramActivationRequest(
  data: VerifyTelegramActivationDto, initData: string, requireCode = false,
): VerifyTelegramActivationDto {
  const activationCode = data.activationCode?.trim();
  if (requireCode && !activationCode) throw new Error("Activation code is required.");
  const identity = telegramLaunchActivationIdentity(data.telegramChatId, initData, data.telegramUserId);
  return { ...identity, ...(activationCode ? { activationCode } : {}) };
}

export function validatePrivateTelegramChatId(chatId: number) {
  validateTelegramChatId(chatId);
  if (chatId < 0) throw new Error("Group authentication is not available yet. Open a private chat through the trusted Telegram bot.");
}

export function resolveTelegramChatId(startParam: string | null | undefined, initData: string): number | null {
  if (startParam?.startsWith("gid_")) {
    const chatId = Number(startParam.slice(4));
    return Number.isSafeInteger(chatId) && chatId !== 0 ? chatId : null;
  }
  try {
    const proof = new URLSearchParams(initData);
    if (!proof.get("hash") || !proof.get("auth_date")) return null;
    const chat = JSON.parse(proof.get("chat") || "null");
    if (chat?.id < 0) return chat.id;
    const user = JSON.parse(proof.get("user") || "null");
    return Number.isSafeInteger(user?.id) && user.id > 0 ? user.id : null;
  } catch { return null; }
}

export function browserTelegramInitData(rawLaunchParams = "") {
  if (typeof window === "undefined") return "";
  const direct = window.Telegram?.WebApp?.initData ||
    new URLSearchParams(window.location.hash?.replace(/^#/, "") || "").get("tgWebAppData") ||
    new URLSearchParams(window.location.search || "").get("tgWebAppData");
  if (direct) return direct;
  const sdkData = new URLSearchParams(rawLaunchParams.replace(/^#/, "")).get("tgWebAppData");
  if (sdkData) return sdkData;
  try {
    const stored = window.sessionStorage?.getItem("tmajs:raw-launch-params") || "";
    return new URLSearchParams(stored.replace(/^#/, "")).get("tgWebAppData") || "";
  } catch { return ""; }
}

export function telegramAuthenticationHeaders(initData: string, telegramChatId?: number, allowGroups = false) {
  const proof = new URLSearchParams(initData);
  if (!proof.get("hash") || !proof.get("auth_date")) {
    throw new Error("Open this app through the trusted Telegram bot. A Telegram ID alone cannot sign you in.");
  }
  const chat = proof.get("chat");
  if (chat) {
    let parsed: { id?: number };
    try { parsed = JSON.parse(chat); } catch { throw new Error("Invalid Telegram chat identity."); }
    if (typeof parsed.id === "number") {
      if (allowGroups) validateTelegramChatId(parsed.id);
      else validatePrivateTelegramChatId(parsed.id);
      if (telegramChatId !== undefined && parsed.id < 0 && parsed.id !== telegramChatId) {
        throw new Error("Telegram chat and authenticated group identity do not match.");
      }
    }
  }
  let user: { id?: number };
  try { user = JSON.parse(proof.get("user") || "null"); } catch { throw new Error("Invalid Telegram user identity."); }
  if (!user || typeof user.id !== "number") throw new Error("A trusted Telegram user identity is required.");
  validatePrivateTelegramChatId(user.id);
  if (telegramChatId !== undefined) {
    if (allowGroups) validateTelegramChatId(telegramChatId);
    else validatePrivateTelegramChatId(telegramChatId);
    if (telegramChatId > 0 && user.id !== telegramChatId) throw new Error("Telegram chat and authenticated user identity do not match.");
  }
  return { "X-Telegram-Init-Data": initData };
}


export function telegramStaffAuthentication(initData: string) {
  // Staff login authenticates the person opening the app, including from a group.
  const headers = telegramAuthenticationHeaders(initData, undefined, true);
  const user = JSON.parse(new URLSearchParams(initData).get("user") || "null") as { id: number; is_bot?: boolean };
  if (user.is_bot) throw new Error("A Telegram bot cannot sign in as a staff user.");
  return { telegramUserId: user.id, headers };
}
